import type { Au08HospitalPremiumDataset } from "./hospital-premiums-types.js";

// AU-08 hospital-cover premium reference — PrivateHealth.gov.au 1 June 2026
// release (the "Private Health Information Statements" dataset published monthly
// on data.gov.au under CC-BY 3.0 AU).
//
// Each figure is DERIVED (median / 25th–75th percentile / counts) over every
// OPEN single-adult hospital policy in the release, grouped by Government cover
// tier ("+" variants folded into their base tier). Premiums are the
// `PremiumNoRebate` field — the monthly premium at the full 100% rate, before
// any Australian Government Rebate — so the figure is comparable across people
// regardless of their rebate tier. A "single" policy is one published with
// `WhoIsCovered OnlyOnePerson="true"`.
//
// Source of truth is the per-policy premiums; nothing here is modelled. Regenerate
// for a new release with `tools/refresh-au08-hospital-premiums.ts`, which streams
// the published XML and prints these paste-ready figures.
//
// Versioned by the release it was built from. NEVER edit a shipped dataset to
// refresh figures — author a new hospital-premiums-YYYY-MM.ts for the next
// release and register it alongside this one (immutability rule, same as the
// rulesets and the APRA benchmark).
//
// The report page that renders this is information, not advice (validation §3):
// it states the typical single-adult cost per tier and cites the source. It
// names no insurer, recommends no tier, and frames no saving.
export const hospitalPremiums2026_06: Au08HospitalPremiumDataset = {
  id: "phis-2026-06",
  period: "PrivateHealth.gov.au, 1 June 2026 release",
  scale: "single",
  premiumBasis: "monthly premium at the full 100% rate, before any rebate",
  sources: {
    dataGovDataset: "https://data.gov.au/data/dataset/private-health-insurance",
    phisRelease:
      "https://data.gov.au/data/dataset/8ab10b1f-6eac-423c-abc5-bbffc31b216c/resource/936e81b1-5855-4bd9-8997-01a1960e5e67/download/privatehealth-01-jun-2026.zip",
    privateHealthGov: "https://www.privatehealth.gov.au",
  },
  sourcesVerifiedAt: "2026-06-13",
  // Derived from "Hospital Open 01-Jun-2026.xml": open single-adult policies,
  // grouped by HospitalTier (base tier), median/quartiles of PremiumNoRebate.
  byTier: {
    Basic: {
      policyCount: 793,
      insurerCount: 27,
      medianMonthlyPremium: 119.55,
      p25MonthlyPremium: 104.61,
      p75MonthlyPremium: 130.86,
    },
    Bronze: {
      policyCount: 1_094,
      insurerCount: 29,
      medianMonthlyPremium: 145.31,
      p25MonthlyPremium: 128.47,
      p75MonthlyPremium: 158.85,
    },
    Silver: {
      policyCount: 2_822,
      insurerCount: 28,
      medianMonthlyPremium: 211.77,
      p25MonthlyPremium: 181.77,
      p75MonthlyPremium: 255.73,
    },
    Gold: {
      policyCount: 1_745,
      insurerCount: 25,
      medianMonthlyPremium: 277.25,
      p25MonthlyPremium: 238.75,
      p75MonthlyPremium: 324.26,
    },
  },
  // The per-tier policyCounts sum to this; distinct insurers across all tiers.
  totalPolicies: 6_454,
  totalInsurers: 32,
};
